import log from "loglevel";

import { Token } from "./definitions";
import contactBackend from "./utils/contactBackend";

const LOCAL_STORAGE_ITEM_ID = "TWILIO_WEBCHAT_WIDGET";
const PRE_ENGAGEMENT_DATA_KEY = "TWILIO_WEBCHAT_PRE_ENGAGEMENT_DATA";

const EXPIRED_SESSION: Token = {
    token: "",
    conversationSid: "",
    identity: "",
    expiration: "0"
};

let _endpoint = "";
/** 菜单进线可写入 localStorage；模板消息进窗只留在内存，关微信后菜单不会自动进聊天窗 */
let persistToStorage = true;
let memorySession: Token | null = null;

function storeSessionData(data: Token) {
    memorySession = data;
    if (!persistToStorage) {
        return;
    }
    localStorage.setItem(LOCAL_STORAGE_ITEM_ID, JSON.stringify(data));
}

function expireLocalSession() {
    memorySession = null;
    localStorage.setItem(LOCAL_STORAGE_ITEM_ID, JSON.stringify(EXPIRED_SESSION));
}

export function getStoredSessionData() {
    const item = localStorage.getItem(LOCAL_STORAGE_ITEM_ID);
    let storedData: Token;

    if (!item) {
        return null;
    }

    try {
        storedData = JSON.parse(item);
    } catch (e) {
        log.log("Couldn't parse locally stored data");
        return null;
    }

    return storedData;
}

function getActiveSessionData(): Token | null {
    if (memorySession?.token) {
        return memorySession;
    }
    return getStoredSessionData();
}

function storePreEngagementData(data: any) {
    localStorage.setItem(PRE_ENGAGEMENT_DATA_KEY, JSON.stringify(data));
}

export function getStoredPreEngagementData() {
    const item = localStorage.getItem(PRE_ENGAGEMENT_DATA_KEY);

    if (!item) {
        return null;
    }

    try {
        return JSON.parse(item);
    } catch (e) {
        log.log("Couldn't parse stored pre-engagement data");
        return null;
    }
}

function clearPreEngagementData() {
    localStorage.removeItem(PRE_ENGAGEMENT_DATA_KEY);
}

export const sessionDataHandler = {
    setEndpoint(endpoint: string = "") {
        _endpoint = endpoint;
    },

    getEndpoint() {
        return _endpoint;
    },

    setPersistToStorage(enabled: boolean) {
        persistToStorage = enabled;
    },

    tryResumeExistingSession(): Token | null {
        log.debug("sessionDataHandler: trying to refresh existing session");
        const storedTokenData = getStoredSessionData();

        if (!storedTokenData) {
            log.debug("sessionDataHandler: no tokens stored, no session to refresh");
            return null;
        }

        if (!storedTokenData.token || Date.now() >= new Date(storedTokenData.expiration).getTime()) {
            log.debug("sessionDataHandler: token expired, ignoring existing sessions");
            return null;
        }

        log.debug("sessionDataHandler: existing token still valid, using existing session data");
        return storedTokenData;
    },

    async getUpdatedToken(): Promise<Token> {
        log.debug("sessionDataHandler: trying to get updated token from BE");
        const storedTokenData = getActiveSessionData();

        if (!storedTokenData?.token) {
            throw Error("Can't update token: current token doesn't exist");
        }

        let newTokenData: Token;

        try {
            newTokenData = await contactBackend<Token>("/refreshToken", {
                token: storedTokenData.token
            });
        } catch (e) {
            throw Error(`Something went wrong when trying to get an updated token: ${e}`);
        }

        // Server won't return a conversation SID, so we merge the existing data with the latest one
        const updatedSessionData = {
            ...storedTokenData,
            ...newTokenData
        };

        storeSessionData(updatedSessionData);

        return updatedSessionData;
    },

    fetchAndStoreNewSession: async ({ formData }: { formData: Record<string, unknown> }) => {
        log.debug("sessionDataHandler: trying to create new session");
        persistToStorage = true;

        let newTokenData;
        console.log("formData", formData);
        try {
            newTokenData = await contactBackend<Token>("/initWebchat", { formData });
        } catch (e) {
            throw Error("No results from server");
        }

        log.debug("sessionDataHandler: new session successfully created");
        storeSessionData(newTokenData);

        return newTokenData;
    },

    /** 模板消息直达：签发 token 仅用于本次打开，不写入 localStorage / expiration */
    resumeConversation: async (conversationSid: string) => {
        log.debug("sessionDataHandler: resumeConversation", conversationSid);
        persistToStorage = false;
        const data = await contactBackend<Token>("/resumeConversation", { conversationSid });
        memorySession = data;
        return data;
    },

    expireSession: () => {
        expireLocalSession();
    },

    clear: () => {
        expireLocalSession();
        clearPreEngagementData();
    },

    clearSession: () => {
        expireLocalSession();
    },

    savePreEngagementData: (data: any) => {
        storePreEngagementData(data);
    },

    getPreEngagementData: () => {
        return getStoredPreEngagementData();
    }
};
